import { Dispatch, SetStateAction } from 'react';
import { RendererContextType } from '../../../../contexts/RendererContext';
import { CadLayer, InitialCadLayer, isLoadedCadLayer } from '../../../../contexts/CadLayersContext';

import { createColor, createVector3 } from '../../../../utils/potreeThree';
import { getCadObjectsByIdentifier } from '../../../../hooks/potree/useRenderer';
import { PointCadObject, DistanceCadObject, AreaCadObject, CadObject } from '../../../../potree/utils/CadObject';

export function syncCadObjects({
  viewer,
  cadLayers,
  cadLayersArray,
  setCadLayers,
}: {
  viewer: NonNullable<RendererContextType['viewer']>;
  cadLayers: Record<string, CadLayer | InitialCadLayer | null>;
  cadLayersArray: Array<CadLayer | InitialCadLayer>;
  setCadLayers: Dispatch<SetStateAction<Record<string, CadLayer | InitialCadLayer | null>>>;
}): void {
  const currentMeasurements = viewer.scene.measurements.filter((measure) => measure.finished);
  const currentMeasurementsByIdentifier = Object.fromEntries(currentMeasurements.map((m) => [m.identifier, m]));
  const cadObjectsByIdentifier = getCadObjectsByIdentifier(cadLayersArray);

  cadLayersArray.forEach((layer) => {
    if (layer.status === 'INIT') return;
    layer.cadObjectGroups?.forEach((cadObjectGroup) => {
      cadObjectGroup.cadObjects.forEach((cadObject) => {
        const color = cadObject.color ? createColor(`rgb(${cadObject.color.r}, ${cadObject.color.g}, ${cadObject.color.b})`) : createColor(1, 0, 0);
        if (cadObject.type === 'POINT') {
          const visible = cadObject.visible;
          const currentMeasurement = currentMeasurementsByIdentifier[cadObject.identifier];
          if (currentMeasurement) {
            currentMeasurement.visible = visible;
            return;
          }
          const position = createVector3(cadObject.position.x, cadObject.position.y, cadObject.position.z);
          const measure = new PointCadObject({
            viewer,
            point: { position },
            visible,
            color,
            finished: true,
            identifier: cadObject.identifier,
            selected: false,
          });
          viewer.scene.addMeasurement(measure);
        } else if (cadObject.type === 'LINE') {
          const visible = cadObject.visible;
          const currentMeasurement = currentMeasurementsByIdentifier[cadObject.identifier];
          if (currentMeasurement) {
            currentMeasurement.visible = visible;
            return;
          }
          const measure = new DistanceCadObject({
            viewer,
            points: cadObject.points.map((p) => ({
              position: createVector3(p.x, p.y, p.z),
            })),
            visible,
            color,
            finished: true,
            identifier: cadObject.identifier,
            selected: false,
          });
          viewer.scene.addMeasurement(measure);
        } else if (cadObject.type === 'POLYGON') {
          const visible = cadObject.visible;
          const currentMeasurement = currentMeasurementsByIdentifier[cadObject.identifier];
          if (currentMeasurement) {
            currentMeasurement.visible = visible;
            return;
          }
          const measure = new AreaCadObject({
            viewer,
            points: cadObject.points.map((p) => ({
              position: createVector3(p.x, p.y, p.z),
            })),
            visible,
            color,
            finished: true,
            identifier: cadObject.identifier,
            selected: false,
          });
          viewer.scene.addMeasurement(measure);
        }
      });
    });
    const cadLayerState = cadLayers[layer.identifier];

    if (isLoadedCadLayer(cadLayerState) && cadLayerState.status !== 'LOADED') {
      setCadLayers((layers) => ({ ...layers, [layer.identifier]: { ...cadLayerState, status: 'LOADED' } }));
    }
  });

  currentMeasurements.forEach((measurement) => {
    if (measurement instanceof CadObject && !cadObjectsByIdentifier[measurement.identifier]) {
      viewer.scene.removeMeasurement(measurement);
    }
  });
}
